import React, { useMemo, useState, useEffect } from 'react';
import './App.css';
import QueryProvider from './app/providers/QueryProvider';
import PriceCoverageSummary from './features/stats/components/PriceCoverageSummary';
import GrowthTable from './features/growth/components/GrowthTable';
import BacktestingView from './features/backtesting/components/BacktestingView';
import IbkrCommModeToggle from './app/components/IbkrCommModeToggle';

export default function App() {
  const storedState = useMemo(() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem('app_sidebar_state_v1');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => storedState?.collapsed ?? true);
  const [activeView, setActiveView] = useState(() => {
    if (typeof window === 'undefined') return 'growth';
    return window.location.hash === '#backtesting' ? 'backtesting' : 'growth';
  });

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem('app_sidebar_state_v1', JSON.stringify({ collapsed: next }));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (activeView === 'backtesting') {
      window.location.hash = 'backtesting';
    } else {
      window.location.hash = 'growth';
    }
  }, [activeView]);

  const viewMeta = activeView === 'backtesting'
    ? {
        title: 'Backtesting',
        description: 'Replay your profile with historical prices to see how the rules would have performed.',
      }
    : {
        title: 'Growth Analytics',
        description:
          'Screen symbols with up-to-date pricing and fundamentals. Hover a symbol to preview the last year of prices, or pin it to compare side-by-side.',
      };

  return (
    <QueryProvider>
      <main className={`app-shell${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
        <aside className="sidebar">
          <button
            type="button"
            className="sidebar-toggle"
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={toggleSidebar}
          >
            {sidebarCollapsed ? '☰' : '×'}
          </button>
          <div className="sidebar-inner">
            <div className="brand">
              <h1>Market Coverage</h1>
              <p className="subtle">Realtime IBKR health</p>
            </div>
            <IbkrCommModeToggle />
            <PriceCoverageSummary compact />
            <nav className="sidebar-nav">
              <button
                type="button"
                className={`nav-item${activeView === 'growth' ? ' active' : ''}`}
                onClick={() => setActiveView('growth')}
              >
                Growth Analytics
              </button>
              <button
                type="button"
                className={`nav-item${activeView === 'backtesting' ? ' active' : ''}`}
                onClick={() => setActiveView('backtesting')}
              >
                Backtesting
              </button>
            </nav>
          </div>
        </aside>
        <section className="content" id={activeView}>
          <header className="content-header">
            <h2>{viewMeta.title}</h2>
            <p className="subtle">{viewMeta.description}</p>
          </header>
          {activeView === 'backtesting' ? <BacktestingView /> : <GrowthTable />}
        </section>
      </main>
    </QueryProvider>
  );
}
